/**
 * BYOK Credential Service
 *
 * Frontend service for managing per-user BYOK provider credentials.
 * Talks to the backend `/api/users/me/credentials` endpoints (see
 * server/routes/users.ts). Key material is never returned by the API — only
 * sanitized summaries.
 */

import { getApiBase } from '../config/api';
import { authFetch } from '../utils/authFetch';

/** Sanitized credential summary returned by GET /api/users/me/credentials. */
export interface ByokCredentialSummary {
  provider: string;
  label: string;
  is_active: boolean;
  updated_at: string;
}

/** BYOK providers the user can configure their own key for. */
export type ByokProvider =
  | 'openai'
  | 'anthropic'
  | 'google'
  | 'falai'
  | 'xai'
  | 'ollama'
  | 'piapi';

export const BYOK_PROVIDERS: ByokProvider[] = [
  'openai',
  'anthropic',
  'google',
  'falai',
  'xai',
  'ollama',
  'piapi',
];

/** Friendly labels for the provider dropdown. */
export const BYOK_PROVIDER_LABELS: Record<ByokProvider, string> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic (Claude)',
  google: 'Google (Gemini/Veo)',
  falai: 'fal.ai',
  xai: 'xAI (Grok)',
  ollama: 'Ollama Cloud',
  piapi: 'PiAPI',
};

/** Thrown when the Free-tier API-key cap is hit (HTTP 403, code KEY_LIMIT_REACHED). */
export class ByokKeyLimitError extends Error {
  code = 'KEY_LIMIT_REACHED' as const;
  constructor(message: string) {
    super(message);
    this.name = 'ByokKeyLimitError';
  }
}

class ByokService {
  private apiBase: string;

  constructor() {
    this.apiBase = getApiBase();
  }

  /** List the current user's configured providers (active only, no key material). */
  async listCredentials(): Promise<ByokCredentialSummary[]> {
    const res = await authFetch(`${this.apiBase}/api/users/me/credentials`);
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to load API keys');
    }
    const data = await res.json();
    const credentials: ByokCredentialSummary[] = data.credentials || [];
    // The backend list does not filter inactive rows — only surface active keys.
    return credentials.filter(c => c.is_active);
  }

  /** Store/replace a BYOK provider credential. */
  async setCredential(provider: string, apiKey: string, label?: string): Promise<void> {
    const res = await authFetch(`${this.apiBase}/api/users/me/credentials`, {
      method: 'POST',
      body: JSON.stringify({ provider, apiKey, label }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      if (res.status === 403 && err.code === 'KEY_LIMIT_REACHED') {
        throw new ByokKeyLimitError(err.error || 'API key limit reached');
      }
      throw new Error(err.error || 'Failed to save API key');
    }
  }

  /** Remove (deactivate) a BYOK provider credential. */
  async removeCredential(provider: string): Promise<void> {
    const res = await authFetch(
      `${this.apiBase}/api/users/me/credentials/${encodeURIComponent(provider)}`,
      { method: 'DELETE' },
    );
    if (!res.ok && res.status !== 204) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to delete API key');
    }
  }
}

export const byokService = new ByokService();
export default byokService;
